import React, { useEffect, useRef, useState } from 'react';
import Window from '../os/Window';
import songArt from '../../assets/music/songArt.png';

/** Standalone dev (no parent): approximate bar length if no sync. */
const FALLBACK_DURATION_SEC = 15 * 60;

/** Window chrome + content tuned so play/pause fits without scrolling on the in-desk iframe. */
const MUSIC_WINDOW_W = 300;
const MUSIC_WINDOW_H = 384;

export interface MusicAppProps extends WindowAppProps {
    siteMusicPlaying?: boolean;
    gameBlocksPlayback?: boolean;
    onTogglePlayPause?: () => void;
}

function formatTime(totalSec: number) {
    const s = Math.max(0, Math.floor(totalSec));
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${r < 10 ? '0' : ''}${r}`;
}

function PlayGlyph() {
    return (
        <svg width={36} height={36} viewBox="0 0 36 36" fill="none" aria-hidden>
            <circle cx="18" cy="18" r="17" fill="rgba(255,255,255,0.12)" />
            <path d="M14 11l11 7-11 7V11z" fill="#fff" />
        </svg>
    );
}

function PauseGlyph() {
    return (
        <svg width={36} height={36} viewBox="0 0 36 36" fill="none" aria-hidden>
            <circle cx="18" cy="18" r="17" fill="rgba(255,255,255,0.12)" />
            <rect x="12" y="11" width="4" height="14" rx="1" fill="#fff" />
            <rect x="20" y="11" width="4" height="14" rx="1" fill="#fff" />
        </svg>
    );
}

const MusicApp: React.FC<MusicAppProps> = (props) => {
    const siteMusicPlaying = props.siteMusicPlaying ?? true;
    const gameBlocksPlayback = props.gameBlocksPlayback ?? false;
    const onTogglePlayPause = props.onTogglePlayPause ?? (() => {});
    const playing = siteMusicPlaying && !gameBlocksPlayback;

    const isEmbedded =
        typeof window !== 'undefined' && window.self !== window.top;

    const [remote, setRemote] = useState<{ current: number; duration: number } | null>(
        null
    );
    const [localPhase, setLocalPhase] = useState(0);
    const localStartRef = useRef<number | null>(null);

    useEffect(() => {
        const onMsg = (e: MessageEvent) => {
            if (e.data?.type !== 'music-progress') return;
            const duration = Number(e.data.duration);
            const current = Number(e.data.current);
            if (!Number.isFinite(duration) || duration <= 0) return;
            setRemote({
                duration,
                current: Number.isFinite(current) ? Math.max(0, current) : 0,
            });
        };
        window.addEventListener('message', onMsg);
        return () => window.removeEventListener('message', onMsg);
    }, []);

    useEffect(() => {
        if (isEmbedded) return;
        if (!playing) {
            localStartRef.current = null;
            return;
        }
        let raf = 0;
        const tick = (ts: number) => {
            if (localStartRef.current == null) localStartRef.current = ts;
            const elapsed = (ts - localStartRef.current) / 1000;
            const d = remote?.duration ?? FALLBACK_DURATION_SEC;
            setLocalPhase((elapsed % d) / d);
            raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [playing, isEmbedded, remote?.duration]);

    const durationSec = remote?.duration ?? FALLBACK_DURATION_SEC;
    const currentSec = isEmbedded
        ? remote != null
            ? remote.current
            : 0
        : localPhase * (remote?.duration ?? FALLBACK_DURATION_SEC);

    const ratio = durationSec > 0 ? Math.min(1, currentSec / durationSec) : 0;
    const remainingSec = Math.max(0, durationSec - currentSec);

    return (
        <Window
            top={44}
            left={60}
            width={MUSIC_WINDOW_W}
            height={MUSIC_WINDOW_H}
            windowTitle="Music"
            windowBarIcon="appleMusicIcon"
            windowBarColor="rgba(34, 34, 36, 0.94)"
            closeWindow={props.onClose}
            onInteract={props.onInteract}
            minimizeWindow={props.onMinimize}
        >
            <div style={styles.root}>
                <div style={styles.artCard}>
                    <img src={songArt} alt="" style={styles.art} />
                </div>

                <div style={styles.meta}>
                    <h2 style={styles.songTitle}>Afternoon Jazz [jazz lofi]</h2>
                    <p style={styles.artist}>Lofi Girl</p>
                </div>

                {gameBlocksPlayback ? (
                    <p style={styles.hint}>Paused while a game is open.</p>
                ) : null}

                <div style={styles.timeRow}>
                    <span style={styles.time}>{formatTime(currentSec)}</span>
                    <span style={styles.time}>−{formatTime(remainingSec)}</span>
                </div>

                <div style={styles.scrubTrack} aria-hidden>
                    <span style={styles.scrubBg} />
                    <span
                        style={Object.assign({}, styles.scrubFill, {
                            transform: `scaleX(${ratio})`,
                        })}
                    />
                </div>

                <div style={styles.transport}>
                    <button
                        type="button"
                        style={styles.playMain}
                        className="site-button"
                        onClick={() => {
                            if (!gameBlocksPlayback) onTogglePlayPause();
                        }}
                        disabled={gameBlocksPlayback}
                        title={
                            gameBlocksPlayback
                                ? 'Unavailable during games'
                                : playing
                                  ? 'Pause'
                                  : 'Play'
                        }
                        aria-label={playing ? 'Pause' : 'Play'}
                    >
                        {playing ? <PauseGlyph /> : <PlayGlyph />}
                    </button>
                </div>
            </div>
        </Window>
    );
};

const styles: StyleSheetCSS = {
    root: {
        height: '100%',
        maxHeight: '100%',
        flexDirection: 'column',
        alignItems: 'stretch',
        justifyContent: 'flex-start',
        padding: '10px 14px 12px',
        background: '#000',
        boxSizing: 'border-box',
        overflow: 'hidden',
        minHeight: 0,
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display", "Segoe UI", sans-serif',
    },
    artCard: {
        alignSelf: 'center',
        width: 156,
        height: 156,
        flexShrink: 0,
        borderRadius: 8,
        overflow: 'hidden',
        boxShadow: '0 12px 32px rgba(0,0,0,0.45)',
    },
    art: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        display: 'block',
    },
    meta: {
        marginTop: 10,
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        paddingLeft: 4,
        paddingRight: 4,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
        flexShrink: 0,
    },
    songTitle: {
        margin: 0,
        padding: 0,
        width: '100%',
        maxWidth: '100%',
        fontSize: 14,
        fontWeight: 600,
        lineHeight: 1.3,
        letterSpacing: '-0.02em',
        color: '#fff',
        textAlign: 'center',
        overflowWrap: 'anywhere',
        wordBreak: 'break-word',
    },
    artist: {
        margin: 0,
        padding: 0,
        width: '100%',
        fontSize: 13,
        fontWeight: 400,
        lineHeight: 1.3,
        color: 'rgba(255,255,255,0.55)',
        textAlign: 'center',
        flexShrink: 0,
    },
    hint: {
        margin: '6px 0 0',
        fontSize: 11,
        color: 'rgba(255,180,180,0.85)',
        textAlign: 'center',
        lineHeight: 1.25,
        padding: '0 8px',
        flexShrink: 0,
    },
    timeRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginTop: 8,
        paddingLeft: 2,
        paddingRight: 2,
        flexShrink: 0,
    },
    time: {
        fontSize: 10,
        fontWeight: 500,
        fontVariantNumeric: 'tabular-nums',
        color: 'rgba(255,255,255,0.45)',
    },
    scrubTrack: {
        position: 'relative',
        width: '100%',
        height: 8,
        marginTop: 4,
        flexShrink: 0,
    },
    scrubBg: {
        position: 'absolute',
        left: 0,
        right: 0,
        top: '50%',
        height: 3,
        marginTop: -1.5,
        borderRadius: 2,
        background: 'rgba(255,255,255,0.14)',
    },
    scrubFill: {
        position: 'absolute',
        left: 0,
        top: '50%',
        width: '100%',
        height: 3,
        marginTop: -1.5,
        borderRadius: 2,
        background: '#fff',
        transformOrigin: 'left center',
        pointerEvents: 'none',
    },
    transport: {
        marginTop: 10,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    playMain: {
        border: 'none',
        background: 'transparent',
        padding: 4,
        cursor: 'pointer',
        borderRadius: '50%',
        lineHeight: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
};

export default MusicApp;
